import { v4 } from 'uuid';
import { MS_IN_DAY } from '../../utils/time-utils';
import { FailPopup, FailPopupLayoutType, FailPopupStep } from '../../game/views/popups/failFlow/FailPopup';
import { GAME_ECONOMY_SETTINGS, ReviveReason } from '../../game/GameConstants';
import type { App } from '../../app/App';
import type { IAPProduct } from '../../app/iap/types';
import { iapPlacementData, IAPPlacement } from '../../app/iap/types';
import type { LevelTaskProgress } from '../../game/tasks/types';
import { openOutOfLivesPopup } from '../openOutOfLivesPopup';
import { openShopPopup } from '../openShopPopup';
import type { MetaProperties } from '../../app/tracking/trackingModules/avo/Avo';
import { AdResponse } from '../../app/tracking/trackingModules/avo/Avo';
import type { IAPController } from '../../app/iap/IAPController';
import { OfferLocation, OfferTag, OfferTrigger } from '../../app/offers/offersTypes';
import { findPreferredOffer, getMostExpensivePurchaseCost } from '../../app/offers/offersUtils';

export interface StartFailFlowConfig {
  tasks: LevelTaskProgress[];
  metaProperties?: MetaProperties[];
}

export interface OpenFailFlowPopupConfig {
  app: App;
  unlimitedLives: boolean;
  flowConfig: StartFailFlowConfig;
}

export enum FailFlowResolution {
  ReviveBySpecialOffer = 'ReviveBySpecialOffer',
  ReviveByCoins = 'ReviveByCoins',
  ReviveByAd = 'ReviveByAd',
  ExitToMenu = 'ExitToMenu',
  RestartLevel = 'RestartLevel',
}

async function getFailFlowBannerOffer(iap: IAPController | null): Promise<IAPProduct | null> {
  if (!iap) return null;
  const offers = await iap.getCatalogAsync(OfferLocation.LevelFailBanner, OfferTrigger.LevelFailed);
  const purchaseHistory = await iap.getPurchaseHistoryAsync();

  // Legacy offers goes above all
  const safetyNetOffer = offers.find((offer) => offer.tags.includes(OfferTag.SafetyNet));
  if (safetyNetOffer) return safetyNetOffer;

  const seasonPass = offers.find((offer) => offer.tags.includes(OfferTag.EventPass));
  if (seasonPass) return seasonPass;

  // For the fail flow prioritize offers as such: Starter -> Upsell -> Basic

  const activeStarterOffer = offers.find((offer) => offer.tags.includes(OfferTag.StarterBundle));
  if (activeStarterOffer) return activeStarterOffer;

  const activeUpsellOffer = offers.find((offer) => offer.tags.includes(OfferTag.UpsellBundle));
  if (activeUpsellOffer) return activeUpsellOffer;

  const basicBundleOffers = offers.filter((offer) => offer.tags.includes(OfferTag.BasicBundle));

  const mostExpensivePurchase = getMostExpensivePurchaseCost(purchaseHistory, 10 * MS_IN_DAY);
  const preferredOffer = findPreferredOffer(basicBundleOffers, mostExpensivePurchase);
  if (preferredOffer) return preferredOffer;

  return null;
}

async function openFailFlowPopup(config: OpenFailFlowPopupConfig): Promise<FailFlowResolution> {
  const {
    app: {
      ads,
      stateController,
      resources,
      controller: { popups },
      tracking,
      gameEvents,
    },
    app,
    unlimitedLives,
    flowConfig: { tasks, metaProperties },
  } = config;

  const { resolve, promise } = Promise.withResolvers<FailFlowResolution>();

  const impressionId = v4();

  const adReviveAvailable = !!(ads && stateController.getLevelReviveUsedCount() === 0);

  const failPopupView = new FailPopup({
    state: stateController,
    tasks: config.flowConfig.tasks,
    unlimitedLives,
    lossProgressBadges: gameEvents.createLostProgressBadges(),
    layoutType: adReviveAvailable ? FailPopupLayoutType.AdRevive : FailPopupLayoutType.Default,
  });

  getFailFlowBannerOffer(app.iap).then(
    (offer) => {
      if (!offer || failPopupView.root.destroyed) return;
      tracking.trackUIInteraction({
        action: 'View',
        contextName: iapPlacementData[IAPPlacement.LevelEnd].uiInteractionContext,
        elementId: 'bannerOffer',
        metaProperties: [
          {
            metaPropertyName: 'Product Id',
            metaPropertyValue: offer.id,
          },
          {
            metaPropertyName: 'Impression Id',
            metaPropertyValue: impressionId,
          },
        ],
      });

      failPopupView.addBannerOffer(offer);
    },
    () => {},
  );

  tracking.trackReviveShown({
    levelNumber: stateController.getLevelNumber(),
  });

  failPopupView.onCloseButtonClicked.connect(async (step: FailPopupStep) => {
    if (step === FailPopupStep.Confirmation) {
      onLevelLost(app, tasks, metaProperties);
    }

    if (!(await failPopupView.advancePopupStep())) {
      resolve(FailFlowResolution.ExitToMenu);
    }
  });

  failPopupView.onRetryButtonClicked.connect(async () => {
    if (stateController.getLives() === 0 && !unlimitedLives) {
      await openOutOfLivesPopup({
        app,
        entryPoint: 'fail-popup',
        viewsToMoveAbove: [],
      });
      if (!stateController.hasLivesToPlay()) return;
    }

    popups.closeAllPopups();
    resolve(FailFlowResolution.RestartLevel);
  });

  failPopupView.onPlayButtonClicked.connect(() => {
    const { reviveCoinCost } = GAME_ECONOMY_SETTINGS;
    if (!stateController.canAffordCoinCost(reviveCoinCost)) {
      void openShopPopup(app, IAPPlacement.LevelEnd, true);
      return;
    }

    resources.payForRevive(reviveCoinCost);
    // revive
    failPopupView.root.interactiveChildren = false;

    tracking.trackReviveUsed({
      levelNumber: stateController.getLevelNumber(),
      reason: ReviveReason.Coins,
      resourceAmount: GAME_ECONOMY_SETTINGS.reviveCoinCost,
    });
    resolve(FailFlowResolution.ReviveByCoins);
  });

  failPopupView.onOfferClicked.connect(async (product) => {
    const success =
      (await app.iap?.makePurchase({
        product,
        placement: IAPPlacement.LevelEnd,
        showResults: true,
        offerImpressionId: impressionId,
      })) ?? false;
    if (!success) return;

    tracking.trackReviveUsed({
      levelNumber: stateController.getLevelNumber(),
      reason: ReviveReason.SpecialOffer,
      resourceAmount: 0,
    });

    resolve(FailFlowResolution.ReviveBySpecialOffer);
  });

  failPopupView.onAdButtonClicked.connect(async () => {
    const response = await app?.ads?.showRewardedAd('revive');
    if (response !== AdResponse.SUCCESS) return;
    resolve(FailFlowResolution.ReviveByAd);
  });

  void popups.addPopup({
    popupInstance: failPopupView,
  });

  return promise.then(async (value) => {
    await popups.closeActivePopup();
    return value;
  });
}

function getLevelLostMetaProps(tasks: LevelTaskProgress[]): MetaProperties[] {
  const output: MetaProperties[] = [];

  tasks.forEach((task) => {
    output.push(
      {
        metaPropertyName: `Task ${task.type} goal`,
        metaPropertyValue: `${task.goal}`,
      },
      {
        metaPropertyName: `Task ${task.type} collected`,
        metaPropertyValue: `${task.collected}`,
      },
    );
  });

  return output;
}

function onLevelLost(app: App, tasks: LevelTaskProgress[], metaProperties?: MetaProperties[]): void {
  const {
    stateController,
    resources,
    tracking,
  } = app;

  const unlimitedLives = stateController.getUnlimitedLivesMinutes() > 0;

  app.gameEvents.onLevelLost();

  tracking.trackLevelLost({
    ...stateController.getLevelTrackingData(),
    metaProperties: [...getLevelLostMetaProps(tasks), ...(metaProperties ?? [])],
  });

  if (!unlimitedLives) {
    resources.useLife();
  }

  stateController.resetLevel();
}

export async function startFailFlow(app: App, config: StartFailFlowConfig): Promise<FailFlowResolution> {
  const { stateController, ads } = app;

  const unlimitedLives = stateController.getUnlimitedLivesMinutes() > 0;

  const failPopupResolution = await openFailFlowPopup({
    app,
    unlimitedLives,
    flowConfig: config,
  });

  switch (failPopupResolution) {
    case FailFlowResolution.ExitToMenu:
    case FailFlowResolution.RestartLevel:
      await ads?.showInterstitialAd('lost');
      break;
    default:
      break;
  }

  return failPopupResolution;
}
